type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

/** Margin screen strings (`fin.margin`, `fin.trend`, `fin.breakdown`). */
export const marginPl = {
  margin: {
    subtitle: 'Od sprzedaży do rzeczywistego zysku: koszt towaru, korekty, transport i koszty trasy.',
    kpiSection: 'Wskaźniki marży',
    takeawayProfit: 'Z każdych 100 zł sprzedaży zostaje {value} zł rzeczywistego zysku.',
    takeawayMargin: 'Z każdych 100 zł sprzedaży zostaje {value} zł marży po korektach.',
    waterfall: {
      title: 'Kaskada marży',
      subtitle: 'Gdzie znika sprzedaż: koszt towaru, korekty, transport i inne koszty.',
      subtitleNoCosts: 'Sprzedaż, koszt towaru, marża brutto i korekty.',
    },
    legend: {
      sales: 'Sprzedaż',
      margin: 'Marża',
      cost: 'Odjęcie od wyniku',
      profit: 'Zysk rzeczywisty',
    },
    stepNames: {
      sales: 'Sprzedaż fakturowana',
      cogs: 'Koszt towaru',
      grossMargin: 'Marża brutto',
      corrections: 'Korekty',
      marginAfterCorrections: 'Marża po korektach',
      transport: 'Transport',
      otherCosts: 'Inne koszty',
      realProfit: 'Zysk rzeczywisty',
    },
    steps: {
      title: 'Kroki w liczbach',
      subtitle: 'Dokładne wartości i udział w sprzedaży',
    },
    tip: {
      value: 'Wartość',
      pctOfSales: 'Udział w sprzedaży',
      level: 'Poziom po kroku',
    },
    noCosts: {
      title: 'Koszty transportu i zysk rzeczywisty są ukryte',
      body: 'Widzisz marżę handlową po korektach. Koszty trasy, transport i rzeczywisty zysk są dostępne dla kierownictwa i zarządu.',
    },
  },
  trend: {
    title: 'Trend marży (13 miesięcy)',
    subtitle: 'Marża w złotych (słupki) oraz marża % i rentowność % (linie) w tej samej osi czasu.',
    margin: 'Marża (zł)',
    marginPct: 'Marża %',
    realProfit: 'Zysk rzeczywisty (zł)',
    realProfitPct: 'Rentowność rzeczywista %',
  },
  breakdown: {
    title: 'Marża w przekrojach',
    subtitle: 'Sortuj po dowolnej kolumnie. Zmiana liczona względem poprzedniego okresu.',
    by: { customer: 'Klient', rep: 'Handlowiec', product: 'Produkt', group: 'Grupa' },
    col: {
      customer: 'Klient',
      rep: 'Handlowiec',
      product: 'Produkt',
      group: 'Grupa produktów',
      sales: 'Sprzedaż',
      margin: 'Marża',
      marginPct: 'Marża %',
      delta: 'Zmiana marży',
      corrections: 'Korekty',
      realProfit: 'Zysk rzecz.',
      realProfitPct: 'Rent. %',
    },
    total: 'Razem',
    shown: 'Pokazano {shown} z {total} pozycji',
    noAllocation: 'Dla produktów i grup koszty trasy nie są przypisywane',
    filterRep: 'Pokaż tylko tego handlowca',
  },
};

export const marginEn: Shape<typeof marginPl> = {
  margin: {
    subtitle: 'From sales to real profit: cost of goods, corrections, transport and route costs.',
    kpiSection: 'Margin indicators',
    takeawayProfit: 'Out of every 100 PLN of sales, {value} PLN stays as real profit.',
    takeawayMargin: 'Out of every 100 PLN of sales, {value} PLN stays as margin after corrections.',
    waterfall: {
      title: 'Margin waterfall',
      subtitle: 'Where sales go: cost of goods, corrections, transport and other costs.',
      subtitleNoCosts: 'Sales, cost of goods, gross margin and corrections.',
    },
    legend: {
      sales: 'Sales',
      margin: 'Margin',
      cost: 'Deducted from result',
      profit: 'Real profit',
    },
    stepNames: {
      sales: 'Invoiced sales',
      cogs: 'Cost of goods',
      grossMargin: 'Gross margin',
      corrections: 'Corrections',
      marginAfterCorrections: 'Margin after corrections',
      transport: 'Transport',
      otherCosts: 'Other costs',
      realProfit: 'Real profit',
    },
    steps: {
      title: 'Steps in numbers',
      subtitle: 'Exact values and share of sales',
    },
    tip: {
      value: 'Value',
      pctOfSales: 'Share of sales',
      level: 'Level after step',
    },
    noCosts: {
      title: 'Transport costs and real profit are hidden',
      body: 'You see the trade margin after corrections. Route costs, transport and real profit are available to management and the board.',
    },
  },
  trend: {
    title: 'Margin trend (13 months)',
    subtitle: 'Margin in PLN (bars) with margin % and profitability % (lines) on one timeline.',
    margin: 'Margin (PLN)',
    marginPct: 'Margin %',
    realProfit: 'Real profit (PLN)',
    realProfitPct: 'Real profitability %',
  },
  breakdown: {
    title: 'Margin breakdown',
    subtitle: 'Sort by any column. Change is measured against the previous period.',
    by: { customer: 'Customer', rep: 'Sales rep', product: 'Product', group: 'Group' },
    col: {
      customer: 'Customer',
      rep: 'Sales rep',
      product: 'Product',
      group: 'Product group',
      sales: 'Sales',
      margin: 'Margin',
      marginPct: 'Margin %',
      delta: 'Margin change',
      corrections: 'Corrections',
      realProfit: 'Real profit',
      realProfitPct: 'Prof. %',
    },
    total: 'Total',
    shown: 'Showing {shown} of {total} rows',
    noAllocation: 'Route costs are not allocated to products and groups',
    filterRep: 'Show only this rep',
  },
};
